import { useEffect, useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Alert,
  Badge,
} from 'reactstrap';
import { useReverifyVatId } from './userMutations';
import { isApiError } from '../../api/errors';

/**
 * VAT-Reverify Flow:
 *  - Auf Open: sofort POST .../reverify-vat-id
 *  - Spinner während Request
 *  - Result-Anzeige mit BZSt-Status (valid/invalid) + Firmenfeldern + reverseChargeEligible
 *  - Bei `valid`: grünes Banner; bei `invalid`: rotes Banner mit Hinweis-Text
 */
export function VatReverifyDialog({ user, onClose }) {
  const isOpen = !!user;
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const reverify = useReverifyVatId();

  // BZSt-Check beim Mount auslösen. Initial-State ist `null` per useState-
  // Default — Reset bei Re-Open passiert via Remount durch Caller
  // (`key={user?.id}` o.ä.), nicht durch setState-im-Effect.
  useEffect(() => {
    if (!user) return;
    reverify
      .mutateAsync({ id: user.id })
      .then(setResult)
      .catch((err) => setError(isApiError(err) ? err.message : 'BZSt-Check fehlgeschlagen.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user) return null;

  return (
    <Modal isOpen={isOpen} toggle={reverify.isPending ? undefined : onClose}>
      <ModalHeader toggle={reverify.isPending ? undefined : onClose}>
        VAT-Reverify · #{user.id}
      </ModalHeader>
      <ModalBody>
        <p className="small text-muted">
          BZSt-Live-Check der USt-IdNr. Result wird im Server gecached + Audit-Log
          geschrieben.
        </p>

        {reverify.isPending && (
          <div className="d-flex align-items-center gap-2 text-muted">
            <div className="spinner-border spinner-border-sm" aria-hidden />
            <span>BZSt-Anfrage läuft…</span>
          </div>
        )}

        {error && <Alert color="danger">{error}</Alert>}

        {result && (
          <>
            <Alert color={result.status === 'valid' ? 'success' : 'danger'}>
              <div className="d-flex align-items-center gap-2">
                <Badge color={result.status === 'valid' ? 'success' : 'danger'} pill>
                  {result.status}
                </Badge>
                {result.status === 'valid' ? (
                  <span>VAT-ID wurde vom BZSt bestätigt.</span>
                ) : (
                  <span>VAT-ID wurde vom BZSt abgelehnt.</span>
                )}
              </div>
            </Alert>

            <dl className="row small mb-0">
              {Object.entries(result.fields ?? {}).map(([k, v]) => (
                <Field key={k} label={k} value={v} />
              ))}
              <Field
                label="Reverse-Charge berechtigt"
                value={result.reverseChargeEligible ? 'ja' : 'nein'}
              />
            </dl>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={onClose} disabled={reverify.isPending}>
          Schließen
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function Field({ label, value }) {
  return (
    <>
      <dt className="col-sm-4 text-muted text-uppercase">{label}</dt>
      <dd className="col-sm-8">{value ?? '—'}</dd>
    </>
  );
}
